import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    RENDERQUALITYFACTOR,
    THREED_MINI_MODE,
    TOOL_DRAWER_WIDTH,
    THREED_MODE,
    THREED_MINI_HEIGHT,
    THREED_MINI_WIDTH,
    toggleDesigner,
    updateDesigner,
} from "../../../redux_store/appState";
import { GetColourUpdateString } from "../PaperFunctionHelper";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import { IconButton } from "@material-ui/core";
import { useScene } from "./BabylonSceneProvider";
import { GetShapeUpdateString } from "../PaperFunctionHelper";
import { updateLayout3D } from "../../../redux_store/appStateHelper";
import { API_BASE } from "../../../AppConfig";
import { fetchTimeout } from "../../../components/Common";

export default (props) => {
    const dispatch = useDispatch();
    const reactCanvas = useRef(null);
    const sceneMgr = useScene();

    const { modelFileName, togglePop, background } = props;

    const jobId = useSelector((state) => state.job.id);
    const businessId = useSelector((state) => state.job.BusinessCode);
    const toolDrawerWidth = useSelector((state) => state.app.designer?.[TOOL_DRAWER_WIDTH]);
    const miniMode3D = useSelector((state) => state.app.designer?.[THREED_MINI_MODE]);
    const miniWidth3D = useSelector((state) => state.app.designer?.[THREED_MINI_WIDTH]);
    const miniHeight3D = useSelector((state) => state.app.designer?.[THREED_MINI_HEIGHT]);
    const is3D = useSelector((state) => state.app.designer?.[THREED_MODE]);
    const renderQuality = useSelector((state) => state.app.designer?.designer3D?.[RENDERQUALITYFACTOR]);

    const [newHeight, setNewHeight] = useState(miniHeight3D);
    const [newWidth, setNewWidth] = useState(miniWidth3D);
    const [updateModel, setUpdateModel] = useState(false);
    const [modelLoaded, setModelLoaded] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);

    const modelFileNameRef = useRef(modelFileName);
    const colourHash = GetColourUpdateString(true);
    const colourHashRef = useRef(colourHash);
    const shapeHashRef = useRef();
    const shapeHash = GetShapeUpdateString(true);

    const baseRenderFactor = 3.0;
    const renderFactor = baseRenderFactor - 0.8 * baseRenderFactor * (renderQuality / 10);
    const fallbackLogo =
        window.brand === "Fielders" ? "models/textures/centenary_logo.png" : "models/textures/lysaght_logo_model.png";

    const showEnvironment = background !== "Grid";

    useEffect(() => {
        if (shapeHashRef.current === shapeHash) return;
        shapeHashRef.current = shapeHash;
        dispatch(updateLayout3D());
    }, [shapeHash]);

    useEffect(() => {
        if (modelFileName && modelFileNameRef.current !== modelFileName) {
            modelFileNameRef.current = modelFileName;
            setUpdateModel(true);
        }
    }, [modelFileName]);

    useLayoutEffect(() => {
        if (reactCanvas) {
            sceneMgr.init(reactCanvas.current, togglePop, showEnvironment);
        }
    }, [sceneMgr]);

    useEffect(() => {
        if (!sceneMgr.scene) return;
        sceneMgr.toggleEnvironment(showEnvironment);
    }, [sceneMgr.scene, background]);

    useEffect(() => {
        if (!sceneMgr.scene || !updateModel) return;
        setUpdateModel(false);

        const loadModel = async () => {
            setModelLoaded(false);
            const modelFilePath = `${jobId}/${modelFileName}`;
            await sceneMgr.importModel(modelFilePath);
            setModelLoaded(true);
        };

        loadModel();
    }, [sceneMgr.scene, updateModel, jobId, modelFileName]);

    useEffect(() => {
        if (!logoUrl || !modelLoaded) return;
        const run = async () => {
            await sceneMgr.addLogo(logoUrl);
        };

        run();
    }, [logoUrl, modelLoaded]);

    useEffect(() => {
        if (!sceneMgr.scene || updateModel || colourHashRef.current === colourHash) return;

        colourHashRef.current = colourHash;

        const run = async () => {
            await sceneMgr.paintModel();
        };

        run();
    }, [colourHash, sceneMgr.scene, updateModel]);

    useEffect(() => {
        if (window) {
            const scene = sceneMgr.scene;
            if (scene) {
                scene.getEngine().setHardwareScalingLevel(renderFactor / window.devicePixelRatio);
                scene.getEngine().resize();
            }
        }
    }, [renderQuality, miniMode3D, toolDrawerWidth, miniHeight3D, miniWidth3D]);

    useEffect(() => {
        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

        const handleMouseMove = (e) => {
            if (!window.isResizing3D) return;

            const rotated = window.threed_mini_mode === "rotated";

            const body = document.body;
            let offset = rotated ? body.offsetWidth - e.clientX : e.clientY;

            const min = rotated ? 500 : 200;
            const max = rotated ? body.offsetWidth - 300 : body.offsetHeight - 100;

            offset = clamp(offset, min, max);

            if (rotated) {
                setNewWidth(offset);
                window.threed_mini_width = offset;
            } else {
                setNewHeight(offset);
                window.threed_mini_height = offset;
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    useEffect(() => {
        const handleMouseUp = () => {
            window.isResizing3D = false;

            const rotated = window.threed_mini_mode === "rotated";

            if (rotated && window.threed_mini_width) {
                dispatch(updateDesigner(THREED_MINI_WIDTH, window.threed_mini_width));
            }

            if (!rotated && window.threed_mini_height) {
                dispatch(updateDesigner(THREED_MINI_HEIGHT, window.threed_mini_height));
            }
        };

        window.addEventListener("mouseup", handleMouseUp);
        return () => window.removeEventListener("mouseup", handleMouseUp);
    }, [dispatch]);

    useEffect(() => {
        const fetchLogo = async () => {
            const endpoint = API_BASE() + `business/logo3d?businessId=${businessId}&brand=${window.brand}`;
            try {
                const response = await fetchTimeout(endpoint, { method: "GET" });
                if (!response.ok) {
                    throw new Error();
                }

                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                setLogoUrl(objectUrl);
            } catch (error) {
                setLogoUrl(fallbackLogo);
            }
        };

        fetchLogo();
    }, []);

    const handleMousedown = (e) => {
        window.isResizing3D = true;
    };

    return (
        <>
            {is3D && (
                <IconButton
                    style={
                        false
                            ? {
                                  zIndex: 2,
                                  display: "block",
                                  position: "absolute",
                                  right: toolDrawerWidth + 10 + "px",
                                  bottom: "27vh",
                                  transition: "all 1s ease",
                              }
                            : {
                                  zIndex: 56,
                                  display: "block",
                                  position: "absolute",
                                  right: toolDrawerWidth + 10 + "px",
                                  top: "90px",
                                  transition: "all 1s ease",
                              }
                    }
                    aria-label="delete"
                    size="small"
                    onClick={() => {
                        dispatch(toggleDesigner(THREED_MINI_MODE)).then(() => {
                            if (!miniMode3D) {
                                sceneMgr.centreModel(true);
                            }
                        });
                    }}
                >
                    {miniMode3D ? (
                        <FullscreenIcon
                            style={{
                                color: "rgb(28 28 28)",
                            }}
                            fontSize="48px"
                        />
                    ) : (
                        <FullscreenExitIcon
                            style={{
                                color: "rgb(28 28 28)",
                            }}
                            fontSize="48px"
                        />
                    )}
                </IconButton>
            )}
            <canvas
                style={
                    miniMode3D && is3D
                        ? {
                              zIndex: 1,
                              display: "block",
                              position: "absolute",
                              left: miniMode3D === "rotated" ? "unset" : "80px",
                              right: toolDrawerWidth * 0.75 + "px",
                              top: "60px",
                              backgroundColor: "rgba(255, 255, 255, 255)",
                              width:
                                  miniMode3D === "rotated"
                                      ? miniWidth3D - toolDrawerWidth * 0.75 + "px"
                                      : "-webkit-fill-available",
                              height: miniMode3D === "rotated" ? "100%" : miniHeight3D - 60 + "px",
                              color: "#000",
                              border: "2px solid rgba(0, 0, 0, 0.15)",
                          }
                        : {
                              zIndex: 0,
                              display: "block",
                              position: "absolute",
                              top: "0px",
                              right: "0px",
                              left: "0px",
                              backgroundColor: "rgba(255, 255, 255, 255)",
                              width: "100%",
                              height: "100%",
                              color: "#000",
                          }
                }
                ref={reactCanvas}
            />

            {miniMode3D === "rotated" && is3D && (
                <div
                    id="dragger_3d"
                    onMouseDown={(event) => {
                        handleMousedown(event);
                    }}
                    style={{
                        width: "3px",
                        cursor: "ew-resize",
                        padding: "4px 0 0",
                        borderBottom: "1px solid #ddd",
                        position: "absolute",
                        top: 0,
                        right: newWidth + "px",
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: "#c7ccd0",
                        "-webkit-user-drag": "none",
                        "&:hover": {
                            backgroundColor: "#a7aca0",
                        },
                    }}
                ></div>
            )}
            {miniMode3D === true && is3D && (
                <div
                    id="dragger_3d"
                    onMouseDown={(event) => {
                        handleMousedown(event);
                    }}
                    style={{
                        height: "3px",
                        cursor: "ns-resize",
                        padding: "4px 0 0",
                        borderBottom: "1px solid #ddd",
                        position: "absolute",
                        top: newHeight + "px",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: "#c7ccd0",
                        "-webkit-user-drag": "none",
                        "&:hover": {
                            backgroundColor: "#a7aca0",
                        },
                    }}
                ></div>
            )}
        </>
    );
};
